import type { ReactNode, RefObject } from 'react'
import { createPortal } from 'react-dom'
import { useDialog } from './useDialog'

export interface DrawerProps {
  children: ReactNode
  onClose: () => void
  /** Id of the drawer's title (`aria-labelledby`). */
  labelledBy: string
  /** false: Escape and the backdrop don't close it. Default true. */
  dismissible?: boolean
  initialFocus?: RefObject<HTMLElement | null>
}

/** Right-hand drawer (mockup `ui.tsx` → `Drawer`), full screen at ≤ 640 px, with the Modal's focus handling. */
export function Drawer({ children, onClose, labelledBy, dismissible = true, initialFocus }: DrawerProps) {
  const { dialogRef, onKeyDown, onBackdropMouseDown } = useDialog({
    onClose,
    dismissible,
    ...(initialFocus && { initialFocus }),
  })
  return createPortal(
    // The backdrop click is a mouse shortcut; Escape is the keyboard equivalent (handled in onKeyDown).
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className="overlay drawer-overlay" onMouseDown={onBackdropMouseDown} onKeyDown={onKeyDown}>
      <div
        ref={(el) => {
          dialogRef.current = el
        }}
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
      >
        {children}
      </div>
    </div>,
    document.body,
  )
}
